import { z } from 'zod';

/**
 * Per-user field visibility for the Marketing Reports screen (ONLY that screen).
 *
 * An admin can hide individual metrics from a specific user. Because the admin
 * tables and the API responses share the same Zod-derived types, a metric's
 * "field key" is the SAME leaf property name wherever it appears - so hiding
 * e.g. `pureProfit` removes it from every tile, table and Excel export at once.
 *
 * Enforcement is server-side: the Marketing Reports endpoints deep-null every
 * hidden key in the response for that user (so the value never leaves the API,
 * network tab or export). The front-end additionally hides the tile/column
 * bound to a hidden key so no empty cell is shown.
 *
 * Only metric LEAF keys that are safe to null independently are in the catalog -
 * raw structural counts (query, converted, ...) are deliberately excluded
 * because other cells derive from them.
 */

export type MarketingFieldGroup =
  | 'Profit & margin'
  | 'Spend & cost'
  | 'Efficiency'
  | 'Revenue'
  | 'Ratios';

export interface MarketingFieldDef {
  /** Leaf property name as it appears in the API JSON / shared types. */
  key: string;
  /** Human label shown in the admin toggle list. */
  label: string;
  group: MarketingFieldGroup;
  /** Where it shows up, for the admin's context. */
  hint?: string;
}

export const MARKETING_FIELD_CATALOG: readonly MarketingFieldDef[] = [
  // Profit & margin
  {
    key: 'pureProfit',
    label: 'Pure Profit',
    group: 'Profit & margin',
    hint: 'Overview tile + Profit sheet',
  },
  {
    key: 'salesProfit',
    label: 'Profit (Sales Tracker)',
    group: 'Profit & margin',
    hint: 'Overview/channel tile + Profit sheet + Destinations',
  },
  {
    key: 'profitAfterSpend',
    label: 'Profit after Spend',
    group: 'Profit & margin',
    hint: 'Destinations table',
  },

  // Spend & cost
  {
    key: 'totalSpend',
    label: 'Total Spend',
    group: 'Spend & cost',
    hint: 'Overview tile + Profit sheet',
  },
  {
    key: 'allAccountSpend',
    label: 'Account Spend',
    group: 'Spend & cost',
    hint: 'Profit/Spend sheet',
  },
  { key: 'managementCost', label: 'Management Cost', group: 'Spend & cost', hint: 'Spend sheet' },
  { key: 'expectedSpend', label: 'Expected Spend', group: 'Spend & cost', hint: 'Profit sheet' },
  { key: 'headsTotal', label: 'Cost Heads Total', group: 'Spend & cost', hint: 'Spend sheet' },
  { key: 'spend', label: 'Destination Spend', group: 'Spend & cost', hint: 'Destinations table' },
  {
    key: 'costPerQuery',
    label: 'Cost per Query (CPQ)',
    group: 'Spend & cost',
    hint: 'Overview tile + Profit sheet + Destinations',
  },
  {
    key: 'cpqAtMetaLevel',
    label: 'CPQ (Meta level)',
    group: 'Spend & cost',
    hint: 'Overview tile + Profit sheet',
  },

  // Efficiency
  {
    key: 'roi',
    label: 'ROI',
    group: 'Efficiency',
    hint: 'Overview tile + Profit sheet + Destinations',
  },
  {
    key: 'roas',
    label: 'RoAS',
    group: 'Efficiency',
    hint: 'Overview tile + Profit sheet + Destinations',
  },
  { key: 'cac', label: 'CAC', group: 'Efficiency', hint: 'Overview tile + Profit sheet' },

  // Revenue
  {
    key: 'ttvAmount',
    label: 'TTV Amount (revenue)',
    group: 'Revenue',
    hint: 'Overall/Intl/Domestic slices',
  },
  {
    key: 'avgOrderValue',
    label: 'Avg Order Value',
    group: 'Revenue',
    hint: 'Overview/channel tile',
  },

  // Ratios
  { key: 'conversionPct', label: 'Conversion %', group: 'Ratios' },
  { key: 'contactabilityRatio', label: 'Query to Contactability %', group: 'Ratios' },
  { key: 'activeRatio', label: 'Query to Active %', group: 'Ratios' },
  { key: 'warmRatio', label: 'Query to Warm %', group: 'Ratios' },
  { key: 'contactedPct', label: 'Contacted %', group: 'Ratios', hint: 'Contactability tables' },
  { key: 'activePct', label: 'Active %', group: 'Ratios', hint: 'Contactability tables' },
  {
    key: 'contactToActivePct',
    label: 'Contact to Active %',
    group: 'Ratios',
    hint: 'Contactability tables',
  },
  {
    key: 'whatsAppToActivePct',
    label: 'WhatsApp to Active %',
    group: 'Ratios',
    hint: 'Contactability tables',
  },
  {
    key: 'wonPct',
    label: 'Converted %',
    group: 'Ratios',
    hint: 'Campaign wise Contactibility',
  },
] as const;

/** Valid catalog keys, for validation + the deep-null allow-list. */
export const MARKETING_FIELD_KEYS: readonly string[] = MARKETING_FIELD_CATALOG.map((f) => f.key);

const MARKETING_FIELD_KEY_SET = new Set(MARKETING_FIELD_KEYS);

/** True when `key` is a known, hideable marketing field. */
export function isMarketingFieldKey(key: string): boolean {
  return MARKETING_FIELD_KEY_SET.has(key);
}

export const MARKETING_FIELD_GROUP_ORDER: readonly MarketingFieldGroup[] = [
  'Profit & margin',
  'Spend & cost',
  'Efficiency',
  'Revenue',
  'Ratios',
];

/**
 * Per-user hidden-field list. `hidden` holds catalog keys the user may NOT see.
 * Unknown keys are dropped on write so the stored list stays in sync with the
 * catalog.
 */
export const MarketingFieldVisibilitySchema = z.object({
  hidden: z.array(z.string()),
});
export type MarketingFieldVisibility = z.infer<typeof MarketingFieldVisibilitySchema>;

/** Request body for setting a user's hidden marketing fields. */
export const SetMarketingFieldVisibilityRequestSchema = z.object({
  hidden: z.array(z.string()),
});
export type SetMarketingFieldVisibilityRequest = z.infer<
  typeof SetMarketingFieldVisibilityRequestSchema
>;
